import { configs } from '$configs';
import { fetchApi } from '$gbusiness/services/api';
import FileFolderModel, { deriveRawToFileFolder } from '../../models/fileFolder';

// Plain async helpers rather than thunks: folders are screen-local state, and
// the Files screen is the only consumer. Each returns a discriminated result so
// callers can surface the server's own message instead of a generic failure.

type Fail = { ok: false; message: string };
type Ok<T> = { ok: true; data: T };

const fail = (response, fallback: string): Fail => ({
  ok: false,
  message: response?.message || fallback,
});

export async function fetchFolders(): Promise<Array<FileFolderModel>> {
  const response = await fetchApi({ url: configs.api.file.folder, method: 'GET' });
  if (!response?.success) return [];
  return (response.list || []).map(deriveRawToFileFolder);
}

export async function createFolder(
  name: string,
  parentId: number | null,
): Promise<Ok<FileFolderModel> | Fail> {
  const response = await fetchApi({
    url: configs.api.file.folder,
    method: 'POST',
    param: { name, ...(parentId ? { parentId } : {}) },
  });
  if (!response?.success) return fail(response, 'Could not create that folder.');
  return { ok: true, data: deriveRawToFileFolder(response.data) };
}

export async function renameFolder(id: number, name: string): Promise<Ok<FileFolderModel> | Fail> {
  const response = await fetchApi({
    url: `${configs.api.file.folder}/${id}`,
    method: 'PUT',
    param: { name },
  });
  if (!response?.success) return fail(response, 'Could not rename that folder.');
  return { ok: true, data: deriveRawToFileFolder(response.data) };
}

export async function moveFolder(
  id: number,
  parentId: number | null,
): Promise<Ok<FileFolderModel> | Fail> {
  const response = await fetchApi({
    url: `${configs.api.file.folder}/${id}/move`,
    method: 'PUT',
    param: { parentId: parentId || null },
  });
  // The server refuses cycles and depth-cap overflows with a reason -- pass it
  // through so the user is told why the move was rejected.
  if (!response?.success) return fail(response, 'Could not move that folder.');
  return { ok: true, data: deriveRawToFileFolder(response.data) };
}

/**
 * Delete a folder. Non-empty folders are refused unless moveContentsTo is given
 * (null means "move contents to the top level").
 */
export async function deleteFolder(
  id: number,
  moveContentsTo?: number | null,
): Promise<Ok<true> | Fail> {
  const response = await fetchApi({
    url: `${configs.api.file.folder}/${id}`,
    method: 'DELETE',
    param: moveContentsTo === undefined ? {} : { moveContentsTo },
  });
  if (!response?.success) return fail(response, 'Could not delete that folder.');
  return { ok: true, data: true };
}

/** Move files into a folder (null = top level). */
export async function moveFilesToFolder(
  fileIds: Array<number>,
  folderId: number | null,
): Promise<boolean> {
  const results = await Promise.all(
    fileIds.map((id) =>
      fetchApi({
        url: `${configs.api.file.general}/${id}`,
        method: 'PUT',
        param: { folderId: folderId || null },
      }),
    ),
  );
  return results.every((r) => r?.success);
}
